import {FC, useEffect, useState} from 'react';
import {FileWithPath} from 'react-dropzone';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Box,
  Autocomplete,
  Button,
  Grid2 as Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import {array, mixed, object, string} from 'yup';

import {
  Category,
  CategorySelect,
  SubCategory,
  SubCategorySelect,
  useGetCategories,
  useGetSubCategories,
} from '@entities/category';
import {useUploadAdminFile} from '@entities/common';
import {
  getParticipant,
  Participant,
  participantNations,
  useCreateParticipant,
  useSetDefaultParticipantName,
  useUpdateParticipantNames,
  useUpdateParticipantLocalizedName,
  useUpsertParticipantInfo,
} from '@entities/participant';
import {getErrorMessageFromApiError} from '@shared/api';
import {imageFileTypes, ImageUploadField} from '@shared/components';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {openAddTranslationsDialog} from '@widgets/common/AddTranslationsDialog';
import {FormRow} from '../../common/FormRow';

type FormValues = {
  localizedName: string;
  names: {value: string}[];
  abbreviation: string;
  nation: string;
  colors: {value: string}[];
  category: Category | null;
  subCategory: SubCategory | null;
  logoFile: FileWithPath | null;
  logoFileStroke: FileWithPath | null;
  logoFilePlayer: FileWithPath | null;
  logoFileCustom: FileWithPath | null;
};

const editSchema = object().shape({
  localizedName: string().required('Required'),
  names: array()
    .of(object().shape({value: string().required('Required')}))
    .min(1)
    .required(),
  abbreviation: string().defined(),
  nation: string().required('Required'),
  colors: array()
    .of(object().shape({value: string().required('Required')}))
    .defined(),
  category: mixed<Category>()
    .required()
    .nullable()
    .notOneOf([null], 'required'),
  subCategory: mixed<SubCategory>().nullable().defined(),
  logoFile: mixed<FileWithPath>().nullable().defined(),
  logoFileStroke: mixed<FileWithPath>().nullable().defined(),
  logoFilePlayer: mixed<FileWithPath>().nullable().defined(),
  logoFileCustom: mixed<FileWithPath>().nullable().defined(),
});

const createSchema = object().shape({
  localizedName: string().required('Required'),
  names: array()
    .of(object().shape({value: string().required('Required')}))
    .min(1)
    .required(),
  abbreviation: string().defined(),
  nation: string().required('Required'),
  colors: array()
    .of(object().shape({value: string().required('Required')}))
    .defined(),
  category: mixed<Category>()
    .required()
    .nullable()
    .notOneOf([null], 'required'),
  subCategory: mixed<SubCategory>().nullable().defined(),
  logoFile: mixed<FileWithPath>().nullable().defined(),
  logoFileStroke: mixed<FileWithPath>().nullable().defined(),
  logoFilePlayer: mixed<FileWithPath>().nullable().defined(),
  logoFileCustom: mixed<FileWithPath>().nullable().defined(),
});

interface Props {
  participant?: Participant;
  onCreated?: () => void;
}

const verifyParticipantNames = (
  saved: Participant,
  names: string[],
  localizedName: string,
) => {
  const savedNames = [...saved.names].sort();
  const submittedNames = [...names].sort();
  if (JSON.stringify(savedNames) !== JSON.stringify(submittedNames)) {
    throw new Error(
      `The server saved ${saved.names.length} of ${names.length} names.`,
    );
  }
  if (saved.localizedName[SupportedLanguage.English] !== localizedName) {
    throw new Error('The server did not save the localized name.');
  }
};

export const EditParticipantForm: FC<Props> = ({participant, onCreated}) => {
  const isCreateMode = !participant;
  const {enqueueSnackbar} = useSnackbar();
  const [isLogoCleared, setIsLogoCleared] = useState(false);
  const [isLogoStrokeCleared, setIsLogoStrokeCleared] = useState(false);
  const [isLogoPlayerCleared, setIsLogoPlayerCleared] = useState(false);
  const [isLogoCustomCleared, setIsLogoCustomCleared] = useState(false);
  const [localizedNameTranslations, setLocalizedNameTranslations] =
    useState<TranslationDict>(
      () => participant?.localizedName ?? ({} as TranslationDict),
    );

  const {mutateAsync: createParticipant, isPending: isCreating} =
    useCreateParticipant();
  const {mutateAsync: updateNames, isPending: isUpdatingNames} =
    useUpdateParticipantNames();
  const {
    mutateAsync: updateLocalizedName,
    isPending: isUpdatingLocalizedName,
  } = useUpdateParticipantLocalizedName();
  const {mutateAsync: upsertInfo, isPending: isUpsertingInfo} =
    useUpsertParticipantInfo();
  const {mutateAsync: uploadAdminFile, isPending: isUploadingFiles} =
    useUploadAdminFile();
  const {mutateAsync: setDefaultName, isPending: isSettingDefault} =
    useSetDefaultParticipantName();

  const {data: categories} = useGetCategories();
  const {data: subCategories} = useGetSubCategories(
    participant?.categorySlug ?? '',
    {
      enabled: !!participant?.categorySlug,
    },
  );

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(isCreateMode ? createSchema : editSchema),
    defaultValues: {
      localizedName:
        participant?.localizedName[SupportedLanguage.English] ?? '',
      names: participant?.names.length
        ? participant.names.map(n => ({value: n}))
        : [{value: ''}],
      abbreviation: participant?.abbreviation ?? '',
      nation: participant?.nation ?? '',
      colors: (participant?.colors ?? []).map(c => ({value: c})),
      category: null,
      subCategory: null,
      logoFile: null,
      logoFileStroke: null,
      logoFilePlayer: null,
      logoFileCustom: null,
    },
  });

  const {control, handleSubmit, reset, watch, setValue} = formMethods;

  useEffect(() => {
    if (!participant || !categories) return;
    const category =
      categories.find(c => c.id === participant.categorySlug) ?? null;
    const subCategory =
      participant.subcategorySlug && subCategories
        ? (subCategories.find(
            sc =>
              sc.id === participant.subcategorySlug ||
              sc.slug === participant.subcategorySlug,
          ) ?? null)
        : null;
    setLocalizedNameTranslations(participant.localizedName);
    reset(prev => ({
      ...prev,
      localizedName:
        participant.localizedName[SupportedLanguage.English] ?? '',
      nation: participant.nation ?? '',
      category,
      subCategory,
    }));
  }, [categories, subCategories, participant, reset]);

  const {
    fields: nameFields,
    append: appendName,
    remove: removeName,
  } = useFieldArray({control, name: 'names'});

  const {
    fields: colorFields,
    append: appendColor,
    remove: removeColor,
  } = useFieldArray({control, name: 'colors'});

  const currentCategory = useWatch({control, name: 'category'});
  const currentLocalizedName = watch('localizedName');

  const onSubmit = async (values: FormValues) => {
    const names = values.names.map(n => n.value).filter(Boolean);
    const localizedName = {
      ...localizedNameTranslations,
      [SupportedLanguage.English]: values.localizedName,
    };
    let createdParticipantId: string | null = null;

    try {
      if (isCreateMode) {
        createdParticipantId = await createParticipant({
          categorySlug: values.category?.id ?? '',
          names,
        });
        await upsertInfo({
          participantId: createdParticipantId,
          categorySlug: values.category?.id ?? '',
          nation: values.nation,
        });
        await updateLocalizedName({
          participantId: createdParticipantId,
          localizedName,
        });
        await updateNames({participantId: createdParticipantId, names});
        const saved = await getParticipant(createdParticipantId);
        verifyParticipantNames(saved, names, values.localizedName);
        enqueueSnackbar(
          'Participant created. Open it to set subcategory, colors and abbreviation.',
          {
            variant: 'success',
          },
        );
        onCreated?.();
      } else {
        const [
          uploadedLogo,
          uploadedLogoStroke,
          uploadedLogoPlayer,
          uploadedLogoCustom,
        ] = await Promise.all([
          values.logoFile
            ? uploadAdminFile({file: values.logoFile, type: 'topic_logo'})
            : null,
          values.logoFileStroke
            ? uploadAdminFile({file: values.logoFileStroke, type: 'topic_logo'})
            : null,
          values.logoFilePlayer
            ? uploadAdminFile({file: values.logoFilePlayer, type: 'topic_logo'})
            : null,
          values.logoFileCustom
            ? uploadAdminFile({file: values.logoFileCustom, type: 'topic_logo'})
            : null,
        ]);

        await upsertInfo({
          participantId: participant.participantId,
          categorySlug: values.category?.id ?? '',
          subcategorySlug:
            values.subCategory?.slug ?? values.subCategory?.id ?? '',
          colors: values.colors.map(c => c.value).filter(Boolean),
          abbreviation: values.abbreviation || null,
          nation: values.nation,
          logoUrl:
            uploadedLogo?.key ??
            (isLogoCleared ? null : (participant.logoUrl ?? null)),
          logoUrlStroke:
            uploadedLogoStroke?.key ??
            (isLogoStrokeCleared ? null : (participant.logoUrlStroke ?? null)),
          logoUrlPlayer:
            uploadedLogoPlayer?.key ??
            (isLogoPlayerCleared ? null : (participant.logoUrlPlayer ?? null)),
          logoUrlCustom:
            uploadedLogoCustom?.key ??
            (isLogoCustomCleared ? null : (participant.logoUrlCustom ?? null)),
        });
        await updateLocalizedName({
          participantId: participant.participantId,
          localizedName,
        });
        await updateNames({participantId: participant.participantId, names});
        const saved = await getParticipant(participant.participantId);
        verifyParticipantNames(saved, names, values.localizedName);
        enqueueSnackbar('Participant updated successfully', {
          variant: 'success',
        });
      }
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : error instanceof Error
          ? error.message
          : '';
      const createdIdSuffix = createdParticipantId
        ? ` Participant ID: ${createdParticipantId}`
        : '';
      enqueueSnackbar(
        `Could not ${isCreateMode ? 'create' : 'update'} participant. ${errorMessage}${createdIdSuffix}`,
        {variant: 'error'},
      );
    }
  };

  const isPending =
    isCreating ||
    isUpdatingNames ||
    isUpdatingLocalizedName ||
    isUpsertingInfo ||
    isUploadingFiles;
  const editOnlyTooltip = isCreateMode ? 'Available after creation' : '';

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container rowSpacing={3} columnSpacing={4} mt={2}>
          {/* Left column: Names + Abbreviation + Colors */}
          <Grid size={6}>
            <Stack spacing={3}>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Controller
                  name="localizedName"
                  control={control}
                  render={({field, fieldState}) => (
                    <TextField
                      {...field}
                      label="Name"
                      size="small"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Button
                  size="small"
                  sx={{mt: 0.25, whiteSpace: 'nowrap'}}
                  onClick={() => {
                    void openAddTranslationsDialog({
                      fieldName: 'Name',
                      initialTranslations: localizedNameTranslations,
                      enTrans: currentLocalizedName,
                    })
                      .then(translations => {
                        setLocalizedNameTranslations(translations);
                        setValue(
                          'localizedName',
                          translations[SupportedLanguage.English] ?? '',
                          {shouldDirty: true},
                        );
                      })
                      .catch(() => undefined);
                  }}
                >
                  Translations
                </Button>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Names
                </Typography>
                <Stack spacing={1}>
                  {nameFields.map((field, index) => {
                    const nameValue = field.value;
                    const isDefault =
                      !!participant?.defaultName &&
                      nameValue === participant.defaultName;
                    return (
                      <Box
                        key={field.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Controller
                          name={`names.${index}.value`}
                          control={control}
                          render={({field: f, fieldState}) => (
                            <TextField
                              {...f}
                              size="small"
                              fullWidth
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              slotProps={{
                                input: {
                                  sx: isDefault
                                    ? {fontWeight: 600, bgcolor: 'action.hover'}
                                    : undefined,
                                },
                              }}
                            />
                          )}
                        />
                        {!isCreateMode && (
                          <Tooltip
                            title={
                              isDefault ? 'Default name' : 'Set as default'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={isDefault || isSettingDefault}
                                onClick={() =>
                                  void setDefaultName({
                                    participantId: participant.participantId,
                                    name: nameValue,
                                  })
                                }
                              >
                                {isDefault ? (
                                  <StarIcon
                                    fontSize="small"
                                    sx={{color: 'warning.main'}}
                                  />
                                ) : (
                                  <StarBorderIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => removeName(index)}
                          disabled={nameFields.length === 1}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => appendName({value: ''})}
                  sx={{mt: 1}}
                  variant="text"
                >
                  Add name
                </Button>
              </Box>

              <Tooltip title={editOnlyTooltip} placement="top-start">
                <span>
                  <Controller
                    name="abbreviation"
                    control={control}
                    render={({field, fieldState}) => (
                      <TextField
                        {...field}
                        label="Abbreviation"
                        size="small"
                        fullWidth
                        disabled={isCreateMode}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </span>
              </Tooltip>

              <Tooltip title={editOnlyTooltip} placement="top-start">
                <Box>
                  <Typography
                    variant="body2"
                    color={isCreateMode ? 'text.disabled' : 'text.secondary'}
                    mb={1}
                  >
                    Colors
                  </Typography>
                  <Stack spacing={1}>
                    {colorFields.map((field, index) => (
                      <Box
                        key={field.id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Controller
                          name={`colors.${index}.value`}
                          control={control}
                          render={({field: f, fieldState}) => (
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              flex={1}
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1,
                                  bgcolor: f.value || 'transparent',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  flexShrink: 0,
                                  opacity: isCreateMode ? 0.4 : 1,
                                }}
                              />
                              <TextField
                                {...f}
                                size="small"
                                fullWidth
                                placeholder="#RRGGBB"
                                disabled={isCreateMode}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            </Box>
                          )}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeColor(index)}
                          disabled={isCreateMode}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => appendColor({value: ''})}
                    sx={{mt: 1}}
                    variant="text"
                    disabled={isCreateMode}
                  >
                    Add color
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
          </Grid>

          {/* Right column: Category + Subcategory */}
          <Grid size={6}>
            <Stack spacing={3}>
              <Controller
                name="nation"
                control={control}
                render={({field, fieldState}) => (
                  <Autocomplete
                    options={participantNations}
                    value={
                      participantNations.find(
                        nation => nation.code === field.value,
                      ) ?? null
                    }
                    getOptionLabel={nation => `${nation.name} (${nation.code})`}
                    onChange={(_, nation) => field.onChange(nation?.code ?? '')}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Nation"
                        size="small"
                        required
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="category"
                control={control}
                render={({field, fieldState}) => (
                  <CategorySelect
                    label="Category"
                    error={fieldState.error?.message}
                    {...field}
                  />
                )}
              />

              <Tooltip title={editOnlyTooltip} placement="top-start">
                <span>
                  <Controller
                    name="subCategory"
                    control={control}
                    render={({field, fieldState}) => (
                      <SubCategorySelect
                        label="Sub-category"
                        categoryId={currentCategory?.id ?? ''}
                        disabled={isCreateMode}
                        error={fieldState.error?.message}
                        {...field}
                      />
                    )}
                  />
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {!isCreateMode && (
          <FormRow mt={4} mb={2}>
            <Box display="flex" gap={4} alignItems="flex-start" flexWrap="wrap">
              <ImageUploadField<FormValues>
                fieldName="logoFile"
                label="Logo"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={
                  isLogoCleared
                    ? undefined
                    : (participant?.logoUrl ?? undefined)
                }
                width={160}
                height={160}
                alt="Participant logo"
                onClearExisting={() => setIsLogoCleared(true)}
              />
              <ImageUploadField<FormValues>
                fieldName="logoFileStroke"
                label="Logo stroke"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={
                  isLogoStrokeCleared
                    ? undefined
                    : (participant?.logoUrlStroke ?? undefined)
                }
                width={160}
                height={160}
                alt="Participant logo stroke"
                onClearExisting={() => setIsLogoStrokeCleared(true)}
              />
              <ImageUploadField<FormValues>
                fieldName="logoFilePlayer"
                label="Logo player"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={
                  isLogoPlayerCleared
                    ? undefined
                    : (participant?.logoUrlPlayer ?? undefined)
                }
                width={160}
                height={160}
                alt="Participant logo player"
                onClearExisting={() => setIsLogoPlayerCleared(true)}
              />
              <ImageUploadField<FormValues>
                fieldName="logoFileCustom"
                label="Logo custom"
                acceptedFileTypes={imageFileTypes}
                existingFileUrl={
                  isLogoCustomCleared
                    ? undefined
                    : (participant?.logoUrlCustom ?? undefined)
                }
                width={160}
                height={160}
                alt="Participant logo custom"
                onClearExisting={() => setIsLogoCustomCleared(true)}
              />
            </Box>
          </FormRow>
        )}

        <FormRow width={420} mt={4}>
          <Button type="submit" sx={{flex: 2}} disabled={isPending}>
            {isCreateMode ? 'Create participant' : 'Apply changes'}
          </Button>
        </FormRow>
      </form>
    </FormProvider>
  );
};
